import { TZDateMini } from '@date-fns/tz';
import { isAfter, isSameDay, isValid } from 'date-fns';
import {
  defineMessages,
  type FormatDateOptions,
  type IntlShape,
} from 'react-intl';
import type { RequireAtLeastOne } from 'type-fest';

import {
  formatDate,
  parseTimeAsTimeValue,
  _getTimeframeExpression,
  _parseTimeExpression,
} from '@dynatrace-sdk/units';
import { getTimezone } from '@dynatrace-sdk/user-preferences';

import type { TimeValue, TimeframeV2 } from '../../../core/types/time.js';
import { isDev } from '../../../core/utils/is-node-environment.js';
import type { TimeframeSelectorPresetItemProps } from '../../../filters/timeframe-selector/Item.js';
import type { TimeframeSelectorProps } from '../../../filters/timeframe-selector/TimeframeSelector.js';
import { DateTimePickerType } from '../../date-time-picker/types.js';
import { removePrecision } from '../../date-time-range-picker/helpers.js';

/** @internal */
export const messages = defineMessages({
  ms: {
    description: 'Timeframe formatter: timeframe-millisecond',
    defaultMessage:
      '{prefix, select, past {Last} future {Next} relativeFuture {In} other {}} ' +
      '{offset, plural, one {# millisecond} other {# milliseconds}}' +
      '{suffix}',
    id: '1ierRkr3xtpxyOXK',
  },
  s: {
    description: 'Timeframe formatter: timeframe-second',
    defaultMessage:
      '{prefix, select, past {Last} future {Next} relativeFuture {In} other {}} ' +
      '{offset, plural, one {# second} other {# seconds}}' +
      '{suffix}',
    id: 'DMJP39IduNdB3wO3',
  },
  m: {
    description: 'Timeframe formatter: timeframe-minute',
    defaultMessage:
      '{prefix, select, past {Last} future {Next} relativeFuture {In} other {}} ' +
      '{offset, plural, one {# minute} other {# minutes}}' +
      '{suffix}',
    id: 'SPSPRmp+gABk1LGQ',
  },
  h: {
    description: 'Timeframe formatter: timeframe-hour',
    defaultMessage:
      '{prefix, select, past {Last} future {Next} relativeFuture {In} other {}} ' +
      '{offset, plural, one {# hour} other {# hours}}' +
      '{suffix}',
    id: 'aNWYfuuoyg/TEftI',
  },
  d: {
    description: 'Timeframe formatter: timeframe-day',
    defaultMessage:
      '{prefix, select, past {Last} future {Next} relativeFuture {In} other {}} ' +
      '{offset, plural, one {# day} other {# days}}' +
      '{suffix}',
    id: 'ECZzXUfKYwLWko5n',
  },
  w: {
    description: 'Timeframe formatter: timeframe-week',
    defaultMessage:
      '{prefix, select, past {Last} future {Next} relativeFuture {In} other {}} ' +
      '{offset, plural, one {# week} other {# weeks}}' +
      '{suffix}',
    id: 'p5Aa2CtWutuGwudB',
  },
  M: {
    description: 'Timeframe formatter: timeframe-month',
    defaultMessage:
      '{prefix, select, past {Last} future {Next} relativeFuture {In} other {}} ' +
      '{offset, plural, one {# month} other {# months}}' +
      '{suffix}',
    id: 'jZte4Ia6a1UkBwpv',
  },
  q: {
    description: 'Timeframe formatter: timeframe-quarter',
    defaultMessage:
      '{prefix, select, past {Last} future {Next} relativeFuture {In} other {}} ' +
      '{offset, plural, one {# quarter} other {# quarters}}' +
      '{suffix}',
    id: 'm5z+3UJ4Q3absAi5',
  },
  y: {
    description: 'Timeframe formatter: timeframe-year',
    defaultMessage:
      '{prefix, select, past {Last} future {Next} relativeFuture {In} other {}} ' +
      '{offset, plural, one {# year} other {# years}}' +
      '{suffix}',
    id: 'ly03uOR7VGcWb+pG',
  },
  requiredTimeframe: {
    defaultMessage: 'Empty timeframe.',
    id: 'QsKlSIxE+Yp58CUG',
    description: 'Timeframe selector required timeframe error hint.',
  },
  requiredFrom: {
    defaultMessage: "The 'from' field is required.",
    id: 'VlyElrRtYeguIlZS',
    description: 'Timeframe selector required from field error hint.',
  },
  requiredTo: {
    defaultMessage: "The 'to' field is required.",
    id: 'd7CsKWrzLvzuObN6',
    description: 'Timeframe selector required to field error hint.',
  },
  invalid: {
    description: 'Timeframe formatter: invalid',
    defaultMessage: 'invalid timeframe',
    id: 'eZkDqSXP6kwDUc5d',
  },
  invalidFrom: {
    defaultMessage: "The 'from' field is invalid.",
    description:
      'Error message shown if the from value of a timeframe is invalid',
    id: 'VTvQ7p6QQyABl+XP',
  },
  invalidTo: {
    defaultMessage: "The 'to' field is invalid.",
    id: 'rm7eAPvUS8J1G8TZ',
    description:
      'Error message shown if the to value of a timeframe is invalid',
  },
  invalidTimeframe: {
    defaultMessage: 'Invalid timeframe',
    id: 'U59PIse3upSvDblc',
    description:
      'Error message shown if the from and to values of a timeframe are both invalid',
  },
  invalidOrder: {
    defaultMessage: "'from' date should be before 'to' date",
    id: 'DKFIWE64kHV1ohuk',
    description:
      'Message to display if the from and to dates are in the wrong order.',
  },
  outsideRange: {
    defaultMessage: 'Only dates between {min} → {max} are available.',
    id: 'cBlxys3qfILuKMaG',
    description:
      'Hint to display in the timeframe selector if a min and a max date is set.',
  },
  now: {
    description: 'Timeframe formatter: timeframe-now',
    defaultMessage: 'Now',
    id: 'sAS3aHsvApXTajqr',
  },
  to: {
    description: 'Timeframe formatter: timeframe-to',
    defaultMessage: 'to',
    id: 'ewgHQuXKDMK7CzyQ',
  },
  tomorrow: {
    description: 'Timeframe formatter: timeframe-tomorrow',
    defaultMessage: 'Tomorrow',
    id: 'FbK3miEv64PNHk2r',
  },
  today: {
    description: 'Timeframe formatter: timeframe-today',
    defaultMessage: 'Today',
    id: 'BlGZxVNh87aTe30G',
  },
  yesterday: {
    description: 'Timeframe formatter: timeframe-yesterday',
    defaultMessage: 'Yesterday',
    id: 'r3yl8iiNQEDjjS+I',
  },
  'roundDown-s': {
    description: 'Timeframe formatter: timeframe-roundDown-second',
    defaultMessage:
      '{prefix, select, previous {Previous} next {Next} other {This}}' +
      ' {amount, plural, one {second} other {# seconds}}',
    id: 'znJQdrgHo0UbGJLV',
  },
  'roundDown-m': {
    description: 'Timeframe formatter: timeframe-roundDown-minute',
    defaultMessage:
      '{prefix, select, previous {Previous} next {Next} other {This}}' +
      ' {amount, plural, one {minute} other {# minutes}}',
    id: 'HpRsCrp1SmD+zRFu',
  },
  'roundDown-h': {
    description: 'Timeframe formatter: timeframe-roundDown-hour',
    defaultMessage:
      '{prefix, select, previous {Previous} next {Next} other {This}}' +
      ' {amount, plural, one {hour} other {# hours}}',
    id: 'vEoSirMyZ5RMVdsR',
  },
  'roundDown-d': {
    description: 'Timeframe formatter: timeframe-roundDown-day',
    defaultMessage:
      '{prefix, select, previous {Previous} next {Next} other {This}}' +
      ' {amount, plural, one {day} other {# days}}',
    id: 'V09xUXtpHEVFRBsh',
  },
  'roundDown-w': {
    description: 'Timeframe formatter: timeframe-roundDown-week',
    defaultMessage:
      '{prefix, select, previous {Previous} next {Next} other {This}}' +
      ' {amount, plural, one {week} other {# weeks}}',
    id: 'BfrmGp7APB2nlQRw',
  },
  'roundDown-M': {
    description: 'Timeframe formatter: timeframe-roundDown-month',
    defaultMessage:
      '{prefix, select, previous {Previous} next {Next} other {This}}' +
      ' {amount, plural, one {month} other {# months}}',
    id: 'Ro/9HWdwiItr3Ter',
  },
  'roundDown-q': {
    description: 'Timeframe formatter: timeframe-roundDown-quarter',
    defaultMessage:
      '{prefix, select, previous {Previous} next {Next} other {This}}' +
      ' {amount, plural, one {quarter} other {# quarters}}',
    id: 'V4fGRjKxu0XkK3nm',
  },
  'roundDown-y': {
    description: 'Timeframe formatter: timeframe-roundDown-year',
    defaultMessage:
      '{prefix, select, previous {Previous} next {Next} other {This}}' +
      ' {amount, plural, one {year} other {# years}}',
    id: 'CjpZq81r1HsdNxp0',
  },
  'full-day': {
    description: 'Timeframe formatter: timeframe-full-day',
    defaultMessage: 'Full day',
    id: 'B/DeQ4xCT/IrvyuQ',
  },
  'remaining-day': {
    description: 'Timeframe formatter: timeframe-remaining-day',
    defaultMessage: 'Remaining day',
    id: 'GixTwN/AsVWJprz2',
  },
});

/**@internal */
export function isExpressionString(candidate: string) {
  const [expression] = candidate.match(_getTimeframeExpression()) ?? [];
  return !!expression;
}

/** @internal */
export function getTimeValue(
  candidate?: TimeValue | string | null,
  precision?: 'minutes' | 'seconds' | 'milliseconds',
  type?: DateTimePickerType,
  relativeNow?: number,
): TimeValue | undefined | null {
  if (candidate === undefined) {
    return undefined;
  }

  if (candidate === null) {
    return null;
  }

  if (typeof candidate === 'string') {
    const parsed = parseTimeAsTimeValue(
      candidate,
      relativeNow,
      type === 'date' ? 'day' : precision,
    );

    // Invalid expression returns broken TimeValue to show errors.
    if (!parsed) {
      return { absoluteDate: '', value: candidate, type: 'expression' };
    }

    // Parsing did return a valid TimeValue
    return parsed;
  }

  // In this case the candidate is already a TimeValue
  return candidate;
}

/** @internal */
export function mapToTimeframeV2(
  value:
    | RequireAtLeastOne<{ from: string; to: string }>
    | RequireAtLeastOne<TimeframeV2>,
  precision?: 'minutes' | 'seconds' | 'milliseconds',
): RequireAtLeastOne<TimeframeV2> | null {
  // All parse times here need to use the same _now_ date, to not be subject
  // to millisecond differences during parsing.
  const relativeNow = TZDateMini.tz(getTimezone()).getTime();
  const from = getTimeValue(value.from, precision, 'datetime', relativeNow);
  const to = getTimeValue(value.to, precision, 'datetime', relativeNow);

  // Satisfies the RequiredAtLeastOne
  if (from === undefined && to === undefined) {
    return null;
  }

  return {
    from,
    to,
  } as RequireAtLeastOne<TimeframeV2>;
}

/**
 * You can use the `_parseValue` utility function to convert a Timeframe or TimeframeV2 value into an object containing `from` and `to`
 * `TimeValue`s or null values. If `fallbackToNow` is set to true, the `to` value falls back to `now` in case the to value is empty.
 * @internal
 */
export function parseValue(
  value:
    | RequireAtLeastOne<{ from: string; to: string }>
    | RequireAtLeastOne<TimeframeV2>
    | null,
  fallbackToNow: boolean = false,
  precision?: 'minutes' | 'seconds' | 'milliseconds',
): {
  from: TimeValue | null;
  to: TimeValue | null;
} {
  const timezone = getTimezone();
  const nowDate = {
    value: 'now()',
    absoluteDate: removePrecision(
      TZDateMini.tz(timezone).toISOString(),
      precision,
    ),
    type: 'expression',
  } as TimeValue;

  if (!value) {
    return { from: null, to: fallbackToNow ? nowDate : null };
  }

  const { valueFrom, valueTo } = getFromToValues(value);

  // All parse times here need to use the same _now_ date, to not be subject
  // to millisecond differences during parsing.
  const relativeNow = TZDateMini.tz(timezone).getTime();

  const from = parseTimeAsTimeValue(valueFrom, relativeNow, precision);
  const to =
    fallbackToNow && !valueTo
      ? nowDate
      : parseTimeAsTimeValue(valueTo, relativeNow, precision);

  return { from, to };
}

/**
 * Formatter that displays sanitized time value strings.
 * @internal
 */
export function formatTimeValue(
  input: TimeValue | Omit<TimeValue, 'absoluteDate'> | string | null,
  precision: TimeframeSelectorProps['precision'] = 'minutes',
) {
  if (input === null) {
    return '';
  }

  if (input === 'now()' || input === 'now') {
    return 'Now'; // early exit to prevent parsing if we already know we should return 'Now'
  }

  const timeValue = parseTimeAsTimeValue(
    typeof input == 'string' ? input : input.value,
    Date.now(),
    precision,
  );

  if (timeValue === null) {
    return '';
  }

  const { type, value } = timeValue;

  if (type === 'iso8601') {
    return getFormattedTimeValue(timeValue, precision);
  }

  if (value === 'now()') {
    return 'Now';
  }

  return value.replaceAll('now()', '').replaceAll('now', '').trim();
}

/**
 * Formatter that gets the formatted time value strings.
 * @internal
 */
export function getFormattedTimeValue(
  timeValue: TimeValue | null,
  precision?: TimeframeSelectorProps['precision'],
  showTimeOnly?: boolean,
) {
  if (!timeValue) {
    return '';
  }

  const timezone = getTimezone();
  const formatOptions: FormatDateOptions = {
    // Extract the user-settings timezone to use for the calculation
    timeZone: getTimezone(),
    day: showTimeOnly ? undefined : 'numeric',
    month: showTimeOnly ? undefined : 'short',
    year: showTimeOnly ? undefined : 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: precision === 'minutes' ? undefined : 'numeric',
    fractionalSecondDigits: precision === 'milliseconds' ? 3 : undefined,
  };
  const absoluteDate = timeValue.absoluteDate
    ? new TZDateMini(timeValue.absoluteDate, timezone)
    : null;

  return absoluteDate && isValid(absoluteDate)
    ? formatDate(absoluteDate, formatOptions)
    : '';
}

/**
 * Handle special cases of TimeframeExpressions to be formatted as natural language
 * @internal
 */
function formatTimeframeExpressionInSpecialCases(
  from: string,
  to: string,
  intl: IntlShape,
) {
  const timeDetailsFrom = _parseTimeExpression(from);
  const timeDetailsTo = _parseTimeExpression(to);
  if (!timeDetailsFrom || !timeDetailsTo) {
    return;
  }

  // convert all weekStartExpressions to 'w' to have a unified translation
  let roundDownTranslationIndex = timeDetailsFrom.roundDownUnit;
  switch (timeDetailsFrom.roundDownUnit) {
    case 'w0':
    case 'w1':
    case 'w2':
    case 'w3':
    case 'w4':
    case 'w5':
    case 'w6':
      roundDownTranslationIndex = 'w';
      break;
    default:
      roundDownTranslationIndex = timeDetailsFrom.roundDownUnit;
  }

  // handle 'this' roundDownUnit, eg `@M -> now` = This month
  if (
    !timeDetailsFrom.offsetAmount &&
    timeDetailsFrom.roundDownUnit &&
    !timeDetailsTo.offsetAmount &&
    !timeDetailsTo.roundDownUnit &&
    roundDownTranslationIndex
  ) {
    // handle exception `@d -> now` = Today
    if (timeDetailsFrom.roundDownUnit === 'd') {
      return {
        displayValue: intl.formatMessage(messages.today),
        isInvalid: false,
        hint: '',
      };
    }

    // handle all other cases, eg `@M -> now` = This month
    return {
      displayValue: intl.formatMessage(
        messages[`roundDown-${roundDownTranslationIndex}`],
        {
          prefix: 'this',
          amount: 1, // to display the singular of a unit we need a 1 here
        },
      ),
      isInvalid: false,
      hint: '',
    };
  }

  // handle 'next' roundDownUnit, eg `+1M@M -> +5M@M` = Next 4 months
  if (
    timeDetailsFrom.offsetAmount === 1 &&
    timeDetailsFrom.roundDownUnit &&
    timeDetailsFrom.offsetUnit &&
    timeDetailsTo.offsetAmount > 0 &&
    timeDetailsTo.roundDownUnit &&
    timeDetailsTo.offsetUnit &&
    timeDetailsFrom.roundDownUnit === timeDetailsTo.roundDownUnit &&
    roundDownTranslationIndex === timeDetailsTo.offsetUnit
  ) {
    // handle exception `+1d@d -> +2d@d` = Tomorrow
    if (
      timeDetailsFrom.roundDownUnit === 'd' &&
      timeDetailsTo.offsetAmount === 2
    ) {
      return {
        displayValue: intl.formatMessage(messages.tomorrow),
        isInvalid: false,
        hint: '',
      };
    }

    // handle all other cases, eg `+1M@M -> +5M@M` = Next 4 months
    return {
      displayValue: intl.formatMessage(
        messages[`roundDown-${roundDownTranslationIndex}`],
        {
          prefix: 'next',
          amount: Math.abs(timeDetailsTo.offsetAmount) - 1,
        },
      ),
      isInvalid: false,
      hint: '',
    };
  }

  // handle 'previous' roundDownUnit, eg `-5M@M -> @M` = Previous 5 months
  if (
    timeDetailsFrom.offsetAmount < 0 &&
    timeDetailsFrom.roundDownUnit &&
    timeDetailsFrom.offsetUnit &&
    !timeDetailsTo.offsetAmount &&
    timeDetailsTo.roundDownUnit &&
    timeDetailsFrom.roundDownUnit === timeDetailsTo.roundDownUnit &&
    timeDetailsFrom.offsetUnit === timeDetailsTo.roundDownUnit &&
    roundDownTranslationIndex
  ) {
    // handle exception `-1d@d -> @d` = Yesterday
    if (
      timeDetailsFrom.roundDownUnit === 'd' &&
      timeDetailsFrom.offsetAmount === -1
    ) {
      return {
        displayValue: intl.formatMessage(messages.yesterday),
        isInvalid: false,
        hint: '',
      };
    }

    // handle all other cases, eg `-5M@M -> @M` = Previous 5 months
    return {
      displayValue: intl.formatMessage(
        messages[`roundDown-${roundDownTranslationIndex}`],
        {
          prefix: 'previous',
          amount: Math.abs(timeDetailsFrom.offsetAmount),
        },
      ),
      isInvalid: false,
      hint: '',
    };
  }

  // handle additional special cases:

  // `@d -> +1d@d` = Full day
  if (
    !timeDetailsFrom.offsetAmount &&
    timeDetailsFrom.roundDownUnit === 'd' &&
    timeDetailsTo.offsetAmount === 1 &&
    timeDetailsTo.roundDownUnit === 'd' &&
    timeDetailsTo.offsetUnit === 'd'
  ) {
    return {
      displayValue: intl.formatMessage(messages['full-day']),
      isInvalid: false,
      hint: '',
    };
  }

  // `now -> +1d@d` = Remaining day
  if (
    // It is not great that we parse the whole statement here. We could expose the expression in the
    // _parseTimeExpression function, but since it is in units sdk and would be a breaking change
    // we just wait for moving everything there.
    parseTimeAsTimeValue(from)?.value === 'now()' &&
    timeDetailsTo.offsetAmount === 1 &&
    timeDetailsTo.roundDownUnit === 'd' &&
    timeDetailsTo.offsetUnit === 'd'
  ) {
    return {
      displayValue: intl.formatMessage(messages['remaining-day']),
      isInvalid: false,
      hint: '',
    };
  }
}

/**
 * Returns the display value props for given time values that include relative expressions.
 * @internal
 */
function formatTimeValues(
  from: TimeValue,
  to: TimeValue,
  intl: IntlShape,
  precision: 'minutes' | 'seconds' | 'milliseconds',
  fromDate: Date,
  toDate: Date,
  hint: string,
) {
  if (from.type === 'expression' && to.type === 'expression') {
    if (to.value === from.value) {
      return {
        displayValue: intl.formatMessage(messages.invalid),
        isInvalid: true,
        hint: intl.formatMessage(messages.invalidOrder),
      };
    }

    const specialCase = formatTimeframeExpressionInSpecialCases(
      from.value,
      to.value,
      intl,
    );
    if (specialCase) {
      return specialCase;
    }

    const isFromNow = from.value === 'now()';
    const isToNow = to.value === 'now()';

    if (isToNow || isFromNow) {
      const displayValue = formatRelativeTimeframe(
        isToNow ? from.value : to.value,
        intl,
        isToNow ? 'past' : 'future',
        true,
      );
      return {
        displayValue,
        isInvalid: displayValue === intl.formatMessage(messages.invalid),
        hint: '',
      };
    }
  }

  const isRelativeToNow =
    (to.type === 'expression' && to.value === 'now()') ||
    (from.type === 'expression' && from.value === 'now()');

  // Hide the "to" date if both dates are on the same day. To avoid states like "In 7 days → 12:13:50",
  // only hide the "to" date if both dates are also absolute or still today.
  const hideToDate =
    isSameDay(fromDate, toDate) &&
    ((from.type === 'iso8601' && to.type === 'iso8601') ||
      (from.type === 'expression' && from.value === 'now()'));

  return {
    displayValue: `${formatDetails(
      from,
      intl,
      precision,
      false,
      isRelativeToNow,
    )} → ${formatDetails(to, intl, precision, hideToDate, isRelativeToNow)}`,
    isInvalid: false,
    hint,
  };
}

/**
 * Returns the from and to string values from a TimeframeV2 or its shorthand version.
 * @internal
 */
export function getFromToValues(
  value?:
    | Partial<{ from: string; to: string }>
    | RequireAtLeastOne<TimeframeV2>
    | null,
) {
  if (!value) {
    return {};
  }

  const valueFrom =
    typeof value.from === 'string' ? value.from : value.from?.value;
  const valueTo = typeof value.to === 'string' ? value.to : value.to?.value;

  return { valueFrom, valueTo };
}

/**
 * Returns the display value string for a given timeframe.
 * @internal
 */
export function formatTimeframe(
  value: TimeframeV2 | TimeframeSelectorPresetItemProps['value'] | null,
  intl: IntlShape,
  precision?: 'minutes' | 'seconds' | 'milliseconds',
) {
  const { valueFrom, valueTo } = getFromToValues(value);

  // All parse times here need to use the same _now_ date, to not be subject
  // to millisecond differences during parsing.
  const relativeNow = TZDateMini.tz(getTimezone()).getTime();
  const fromDetails = parseTimeAsTimeValue(valueFrom, relativeNow, precision);
  const toDetails = parseTimeAsTimeValue(valueTo, relativeNow, precision);

  return extractDisplayValueFromTimeValues(
    fromDetails,
    toDetails,
    intl,
    precision,
    valueFrom,
    valueTo,
  );
}

/**
 * Extracts the display value string from the from and to time values.
 * @internal
 */
export function extractDisplayValueFromTimeValues(
  from: TimeValue | null,
  to: TimeValue | null,
  intl: IntlShape,
  precision: 'minutes' | 'seconds' | 'milliseconds' = 'minutes',
  valueFrom?: string,
  valueTo?: string,
) {
  let hint = '';

  if (!from || !to) {
    if (isDev()) {
      if (!from) {
        // eslint-disable-next-line no-console
        console.warn(
          `The from date could not be parsed. Please check if the provided value is valid and supported: ${valueFrom}.`,
        );
      }
      if (!to) {
        // eslint-disable-next-line no-console
        console.warn(
          `The to date could not be parsed. Please check if the provided value is valid and supported: ${valueTo}.`,
        );
      }
    }

    if (!from && !to) {
      hint = intl.formatMessage(messages.invalidTimeframe);
    } else if (!from) {
      hint = intl.formatMessage(messages.invalidFrom);
    } else {
      hint = intl.formatMessage(messages.invalidTo);
    }

    return {
      displayValue: intl.formatMessage(messages.invalid),
      isInvalid: true,
      hint,
    };
  }

  const configuredTimezone = getTimezone();
  const fromDate = new TZDateMini(from.absoluteDate, configuredTimezone);
  const toDate = new TZDateMini(to.absoluteDate, configuredTimezone);

  const { isInvalid, hint: invalidItemHint } = checkInvalidItem(
    fromDate,
    toDate,
    from.value,
    to.value,
    intl,
  );

  if (isInvalid) {
    return {
      displayValue: intl.formatMessage(messages.invalid),
      isInvalid: true,
      hint: invalidItemHint,
    };
  }

  return formatTimeValues(from, to, intl, precision, fromDate, toDate, hint);
}

/**
 * Checks if the preset item is invalid and logs a warning in dev mode.
 * @internal
 */
function checkInvalidItem(
  fromDate: Date,
  toDate: Date,
  from: string,
  to: string,
  intl: IntlShape,
) {
  if (!isValid(fromDate)) {
    if (isDev()) {
      // eslint-disable-next-line no-console
      console.warn(`The from date (${from}) is invalid.`);
    }
    return { isInvalid: true, hint: intl.formatMessage(messages.invalidFrom) };
  }

  if (!isValid(toDate)) {
    if (isDev()) {
      // eslint-disable-next-line no-console
      console.warn(`The to date (${to}) is invalid.`);
    }
    return { isInvalid: true, hint: intl.formatMessage(messages.invalidTo) };
  }

  if (!isAfter(toDate, fromDate)) {
    if (isDev()) {
      // eslint-disable-next-line no-console
      console.warn(
        `The from date - ${from} - should be before the to date - ${to}.`,
      );
    }
    return { isInvalid: true, hint: intl.formatMessage(messages.invalidOrder) };
  }

  return { isInvalid: false, hint: '' };
}

/**
 * Returns expression in e.g. 'Last 5 minutes' format.
 * @internal
 */
function formatRelativeTimeframe(
  expression: string,
  intl: IntlShape,
  prefix?: 'future' | 'past' | 'relativeFuture' | 'other',
  isRelativeToNow?: boolean,
): string {
  const timeDetails = _parseTimeExpression(expression);

  if (timeDetails) {
    const { offsetAmount, offsetUnit, roundDownUnit } = timeDetails;

    if (offsetAmount === 0 && !roundDownUnit) {
      return intl.formatMessage(messages.now);
    }

    // if we have a RoundDownExpression which does not have a natural language equivalent,
    // we opt for the raw expression
    if (roundDownUnit) {
      return expression.replaceAll('now()', '').replaceAll('now', '');
    }

    const timeExpression = messages[offsetUnit as keyof typeof messages];

    if (timeExpression) {
      let suffix = '';
      prefix ??= offsetAmount > 0 ? 'future' : 'past';
      if (prefix === 'future' && !isRelativeToNow) {
        prefix = 'relativeFuture';
      }

      if (!isRelativeToNow && prefix === 'past') {
        suffix = ' ago';
        prefix = 'other'; // used for no prefix in the translations
      }

      return intl
        .formatMessage(timeExpression, {
          prefix,
          offset: Math.abs(offsetAmount),
          suffix,
        })
        .trim();
    }
  }

  return intl.formatMessage(messages.invalid);
}

/**
 * @internal
 */
export function formatDetails(
  details: TimeValue,
  intl: IntlShape,
  precision: TimeframeSelectorProps['precision'] = 'minutes',
  showTimeOnly?: boolean,
  isRelativeToNow?: boolean,
): string {
  return details.type === 'expression'
    ? formatRelativeTimeframe(details.value, intl, undefined, isRelativeToNow)
    : getFormattedTimeValue(details, precision, showTimeOnly);
}

/**
 * Gets the formatted min and max values that are used in the translated messages.
 * @internal
 */
export function getFormattedMinMax(
  min?: string,
  max?: string,
  precision?: TimeframeSelectorProps['precision'],
) {
  // All parse times here need to use the same _now_ date, to not be subject
  // to millisecond differences during parsing.
  const relativeNow = TZDateMini.tz(getTimezone()).getTime();
  const minDate = min ? parseTimeAsTimeValue(min, relativeNow) : null;
  const maxDate = max ? parseTimeAsTimeValue(max, relativeNow) : null;

  const newMinDate = new TZDateMini(minDate?.absoluteDate ?? '', getTimezone());
  // Round up min date depending on the precision.
  if (!isNaN(newMinDate.getTime())) {
    if (precision === 'seconds' && newMinDate.getMilliseconds() > 0) {
      newMinDate.setSeconds(newMinDate.getSeconds() + 1);
    } else if (
      precision === 'minutes' &&
      (newMinDate.getMilliseconds() > 0 || newMinDate.getSeconds() > 0)
    ) {
      newMinDate.setMinutes(newMinDate.getMinutes() + 1);
    }
  }
  const minTimeValue = parseTimeAsTimeValue(
    !isNaN(newMinDate.getTime())
      ? newMinDate.toISOString()
      : minDate?.absoluteDate,
    relativeNow,
  );

  const formattedMin = getFormattedTimeValue(minTimeValue, precision);
  const formattedMax = getFormattedTimeValue(
    maxDate,
    precision,
    !!minTimeValue &&
      !!maxDate &&
      isSameDay(minTimeValue.absoluteDate, maxDate.absoluteDate),
  );

  return {
    min: formattedMin,
    max: formattedMax,
  };
}

/**
 * Gets the translated message to show when a value is outside the min and/or max
 * @internal
 */
export function getOutsideRangeMessage(
  intl: IntlShape,
  min?: string,
  max?: string,
  precision?: TimeframeSelectorProps['precision'],
) {
  const { min: from, max: to } = getFormattedMinMax(min, max, precision);

  if (min && max) {
    return intl.formatMessage(
      {
        defaultMessage: 'Only dates between {from} → {to} are available.',
        id: 'Nlzd/zJ2XP7vZMvE',
        description: 'Given dates are not within the valid range.',
      },
      {
        from,
        to,
      },
    );
  }

  if (min) {
    return intl.formatMessage(
      {
        defaultMessage: 'Only dates after {from} are available.',
        id: 'QXOCOrq5Seu+1A93',
        description: 'Given dates are not within the valid range.',
      },
      {
        from,
      },
    );
  }

  if (max) {
    return intl.formatMessage(
      {
        defaultMessage: 'Only dates before {to} are available.',
        id: 'Jzdt1FVhHSO1Lmg9',
        description: 'Given dates are not within the valid range.',
      },
      {
        to,
      },
    );
  }

  return intl.formatMessage({
    defaultMessage: 'Selected time is outside of the valid range.',
    id: 'r2DiyNP5gUAg0s5f',
    description: 'Given dates are not within the valid range.',
  });
}
